import React, { useState, useEffect } from 'react';
import { LifeBuoy } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { SupportDialog } from './SupportDialog';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface SupportButtonProps {
  firmId: string;
  firmName: string;
  userId: string;
  userName: string;
  userEmail: string;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
}

export function SupportButton({
  firmId,
  firmName,
  userId,
  userName,
  userEmail,
  variant = 'ghost',
  size = 'default',
  showLabel = true,
}: SupportButtonProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (firmId) {
      fetchUnreadCount();
      
      // Poll for updates every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [firmId]);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${firmId}`,
        {
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const tickets = data.tickets || [];
        
        // Count tickets with responses that user hasn't seen yet
        // For now, just count open tickets with last response
        const unread = tickets.filter(
          (t: any) => 
            t.lastResponse && 
            (t.status === 'Waiting for Customer' || t.status === 'In Progress')
        ).length;
        
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching support ticket count:', error);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowDialog(true)}
        className="relative gap-2"
      >
        <LifeBuoy className="h-4 w-4" />
        {showLabel && <span>Support</span>}
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      <SupportDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          fetchUnreadCount(); // Refresh count when dialog closes
        }}
        firmId={firmId}
        firmName={firmName}
        userId={userId}
        userName={userName}
        userEmail={userEmail}
      />
    </>
  );
}
